import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
import { agencyService } from '../services/agencyService';

interface UserRole {
  _id: string;
  userId: string;
  agencyId: string;
  role: string;
  isActive: boolean;
  assignedBy: string;
  assignedAt: string;
  createdAt: string;
  updatedAt: string;
}

interface TeamMember {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  primaryRole:
    | 'admin'
    | 'sales_manager'
    | 'sales_agent'
    | 'rental_agent'
    | 'sales_assistant'
    | 'rental_assistant'
    | 'property_manager'
    | 'photographer'
    | 'styler';
  roles: string[];
  phone: string;
  isEmailVerified: boolean;
  isActive: boolean;
  createdAt: string;
}

interface UserRoleManagerProps {
  open: boolean;
  onClose: () => void;
  user: TeamMember | null;
  agencySlug: string;
  onRoleUpdate: () => void;
}

const UserRoleManager: React.FC<UserRoleManagerProps> = ({
  open,
  onClose,
  user,
  agencySlug,
  onRoleUpdate
}) => {
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newRole, setNewRole] = useState<string>('');
  const [updatingPrimary, setUpdatingPrimary] = useState<string | null>(null);

  const availableRoles = [
    { value: 'admin', label: 'Admin', color: 'primary' as const },
    { value: 'sales_manager', label: 'Sales Manager', color: 'secondary' as const },
    { value: 'sales_agent', label: 'Sales Agent', color: 'success' as const },
    { value: 'rental_agent', label: 'Rental Agent', color: 'success' as const },
    { value: 'sales_assistant', label: 'Sales Assistant', color: 'default' as const },
    { value: 'rental_assistant', label: 'Rental Assistant', color: 'default' as const },
    { value: 'property_manager', label: 'Property Manager', color: 'warning' as const },
    { value: 'photographer', label: 'Photographer', color: 'info' as const },
    { value: 'styler', label: 'Styler', color: 'info' as const }
  ];

  useEffect(() => {
    if (open && user) {
      fetchUserRoles();
    }
  }, [open, user]);

  const fetchUserRoles = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await agencyService.getUserRoles(agencySlug, user._id);
      setUserRoles(response.data.roles || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch user roles');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = async () => {
    if (!user || !newRole) return;

    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await agencyService.addUserRole(agencySlug, user._id, { role: newRole as any });
      setSuccess(`Role ${newRole} added successfully`);
      setNewRole('');
      await fetchUserRoles();
      onRoleUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add role');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveRole = async (role: string) => {
    if (!user) return;

    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await agencyService.removeUserRole(agencySlug, user._id, role);
      setSuccess(`Role ${role} removed successfully`);
      await fetchUserRoles();
      onRoleUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove role');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrimaryRole = async (role: string) => {
    if (!user) return;

    setUpdatingPrimary(role);
    setError(null);
    setSuccess(null);
    
    try {
      await agencyService.updatePrimaryRole(agencySlug, user._id, { primaryRole: role as any });
      setSuccess(`Primary role updated to ${role}`);
      await fetchUserRoles();
      onRoleUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update primary role');
    } finally {
      setUpdatingPrimary(null);
    }
  };

  const getRoleInfo = (role: string) => {
    return availableRoles.find(r => r.value === role) || { value: role, label: role, color: 'default' as const };
  };

  const getAssignedRoles = () => {
    return userRoles.filter(ur => ur.isActive).map(ur => ur.role);
  };

  const getAvailableRolesToAdd = () => {
    const assignedRoles = getAssignedRoles();
    return availableRoles.filter(role => !assignedRoles.includes(role.value));
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6">
            Manage Roles for {user.firstName} {user.lastName}
          </Typography>
          <Chip 
            label={`Primary: ${getRoleInfo(user.primaryRole).label}`}
            color={getRoleInfo(user.primaryRole).color}
            variant="outlined"
          />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Current Roles */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Current Roles
          </Typography>
          <List>
            {userRoles.filter(ur => ur.isActive).map((userRole) => {
              const roleInfo = getRoleInfo(userRole.role);
              const isPrimary = userRole.role === user.primaryRole;
              
              return (
                <ListItem key={userRole._id} divider>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip 
                          label={roleInfo.label}
                          color={roleInfo.color}
                          variant={isPrimary ? "filled" : "outlined"}
                        />
                        {isPrimary && (
                          <Chip 
                            label="Primary"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={`Assigned: ${new Date(userRole.assignedAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}`}
                  />
                  <ListItemSecondaryAction>
                    <Box display="flex" gap={1}>
                      {!isPrimary && (
                        <Tooltip title="Set as Primary Role">
                          <IconButton
                            size="small"
                            onClick={() => handleUpdatePrimaryRole(userRole.role)}
                            disabled={loading || updatingPrimary === userRole.role}
                          >
                            <StarBorderIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isPrimary && (
                        <Tooltip title="Primary Role">
                          <IconButton size="small" disabled>
                            <StarIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!isPrimary && (
                        <Tooltip title="Remove Role">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveRole(userRole.role)}
                            disabled={loading}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Add New Role */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Add New Role
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Select Role</InputLabel>
              <Select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                label="Select Role"
                disabled={loading}
              >
                {getAvailableRolesToAdd().map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip 
                        label={role.label}
                        color={role.color}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddRole}
              disabled={loading || !newRole}
            >
              Add Role
            </Button>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserRoleManager;
